import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UndoIcon from '@mui/icons-material/Undo';

import { fetchPastOrders, fetchInvoice, makeReturnDoc, submitReturn } from '../api/invoice';

function getMaxReturnQty(item) {
  return Math.abs(Number(item?.qty) || 0);
}

function parseReturnQty(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function clampReturnQty(value, max) {
  return Math.min(Math.max(parseReturnQty(value), 0), max);
}

function scaleReturnItem(item, returnQty) {
  const originalQty = getMaxReturnQty(item);
  const ratio = originalQty > 0 ? returnQty / originalQty : 0;
  const qtySign = (Number(item.qty) || 0) < 0 ? -1 : 1;
  const nextItem = {
    ...item,
    qty: qtySign * returnQty,
  };

  ['stock_qty', 'amount', 'base_amount', 'net_amount', 'base_net_amount'].forEach((field) => {
    if (typeof item[field] === 'number') {
      nextItem[field] = item[field] * ratio;
    }
  });

  return nextItem;
}

/**
 * Returns dialog — two-step flow:
 *
 * Step 1: Search and select a past POS Invoice to return against.
 * Step 2: Review items, select which to return, confirm refund.
 */
export default function ReturnsDialog({ open, onClose, onSuccess }) {
  // Step state
  const [step, setStep] = useState('search'); // 'search' | 'review'

  // Search step
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [pastOrders, setPastOrders] = useState([]);

  // Review step
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [returnDoc, setReturnDoc] = useState(null);
  const [returnQuantities, setReturnQuantities] = useState({}); // { idx: qty }
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load recent orders when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function loadRecent() {
      setSearching(true);
      try {
        const orders = await fetchPastOrders({ searchTerm: '', status: 'Paid', limit: 20 });
        if (!cancelled) setPastOrders(orders);
      } catch {
        // ignore — user can still search manually
      } finally {
        if (!cancelled) setSearching(false);
      }
    }

    loadRecent();
    return () => { cancelled = true; };
  }, [open]);

  // Search past orders (works with empty search term too)
  const handleSearch = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const orders = await fetchPastOrders({
        searchTerm: searchTerm.trim(),
        status: 'Paid',
        limit: 20,
      });
      setPastOrders(orders);
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  // Select an invoice to return
  const handleSelectInvoice = async (invoiceName) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the full invoice and generate return doc
      const [invoice, retDoc] = await Promise.all([
        fetchInvoice(invoiceName),
        makeReturnDoc(invoiceName),
      ]);

      setOriginalInvoice(invoice);
      setReturnDoc(retDoc);

      // Default to full return quantities; cashier can reduce per row.
      const quantities = {};
      (retDoc.items || []).forEach((item, idx) => {
        quantities[idx] = String(getMaxReturnQty(item));
      });
      setReturnQuantities(quantities);
      setStep('review');
    } catch (err) {
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnQtyChange = (idx, value, max) => {
    setReturnQuantities((prev) => ({
      ...prev,
      [idx]: value === '' ? '' : String(clampReturnQty(value, max)),
    }));
  };

  // Submit the return
  const handleSubmitReturn = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const itemsToReturn = (returnDoc.items || [])
        .map((item, idx) => scaleReturnItem(item, clampReturnQty(returnQuantities[idx], getMaxReturnQty(item))))
        .filter((item) => Math.abs(item.qty || 0) > 0);
      if (itemsToReturn.length === 0) {
        setError('Enter a return quantity for at least one item.');
        setSubmitting(false);
        return;
      }

      // Build the return document with only selected items
      const doc = {
        ...returnDoc,
        items: itemsToReturn,
      };

      const result = await submitReturn(doc);

      onSuccess?.({
        returnInvoice: result,
        originalInvoice,
      });
    } catch (err) {
      const msg = err?.response?.data?._server_messages || err.message || 'Return failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset when dialog closes
  const handleClose = () => {
    setStep('search');
    setSearchTerm('');
    setPastOrders([]);
    setOriginalInvoice(null);
    setReturnDoc(null);
    setReturnQuantities({});
    setError(null);
    onClose();
  };

  const refundTotal = (returnDoc?.items || []).reduce((sum, item, idx) => {
    const qty = clampReturnQty(returnQuantities[idx], getMaxReturnQty(item));
    return sum + qty * Math.abs(item.rate || 0);
  }, 0);
  const hasReturnQty = (returnDoc?.items || []).some(
    (item, idx) => clampReturnQty(returnQuantities[idx], getMaxReturnQty(item)) > 0,
  );

  return (
    <Dialog open={open} onClose={!submitting ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <UndoIcon />
          <span>{step === 'search' ? 'Return / Exchange' : 'Review Return'}</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Search past invoices */}
        {step === 'search' && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" onClick={handleSearch} disabled={searching}>
                {searching ? <CircularProgress size={20} /> : 'Search'}
              </Button>
            </Stack>

            {loading && (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            )}

            {pastOrders.length > 0 && !loading && (
              <List dense sx={{ maxHeight: 350, overflow: 'auto' }}>
                {pastOrders.map((order) => (
                  <ListItemButton
                    key={order.name}
                    onClick={() => handleSelectInvoice(order.name)}
                  >
                    <ReceiptLongIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={order.name}
                      secondary={`${order.customer || ''} — ${order.grand_total?.toFixed(2) || ''} — ${order.posting_date || ''}`}
                    />
                    <Chip
                      label={order.status}
                      size="small"
                      color={order.status === 'Paid' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </ListItemButton>
                ))}
              </List>
            )}

            {pastOrders.length === 0 && !searching && !loading && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No invoices found
              </Typography>
            )}
          </>
        )}

        {/* Step 2: Review items and confirm return */}
        {step === 'review' && returnDoc && (
          <>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Original Invoice</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {originalInvoice?.name}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography variant="body2">{originalInvoice?.customer}</Typography>
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Enter return quantities
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Sold Qty</TableCell>
                  <TableCell align="right">Return Qty</TableCell>
                  <TableCell align="right">Refund</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(returnDoc.items || []).map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2">{item.item_name || item.item_code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.rate?.toFixed(2)} each
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {getMaxReturnQty(item)}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={returnQuantities[idx] ?? ''}
                        onChange={(e) => handleReturnQtyChange(idx, e.target.value, getMaxReturnQty(item))}
                        inputProps={{
                          min: 0,
                          max: getMaxReturnQty(item),
                          step: '0.001',
                          style: { textAlign: 'right' },
                        }}
                        sx={{ width: 96 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {(clampReturnQty(returnQuantities[idx], getMaxReturnQty(item)) * Math.abs(item.rate || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>Refund Total</Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {refundTotal.toFixed(2)}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'review' && (
          <Button onClick={() => setStep('search')} disabled={submitting} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {step === 'review' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitReturn}
            disabled={submitting || !hasReturnQty}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Process Return'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
